import { store } from '../store';
import { refreshToken as refreshTokenAction, logoutUser } from '../store/slices/authSlice';
import tokenStorage from './tokenStorage';

class TokenManager {
  constructor() {
    this.refreshTimer = null;
    this.isRefreshing = false;
    this.lastRefreshAttempt = 0;
    this.refreshCooldown = 5000; // 5 seconds cooldown between refresh attempts
  }

  // Start automatic token refresh
  startTokenRefresh() {
    // Clear any existing timer
    this.clearRefreshTimer();

    // Don't start token refresh on public pages
    const currentPath = window.location.pathname;
    const isPublic = currentPath === '/login' || currentPath === '/register' || currentPath === '/';

    if (isPublic) {
      return;
    }

    // Check if we have at least an access token
    const accessToken = tokenStorage.getAccessToken();

    if (!accessToken) {
      return;
    }

    // Delay immediate check to avoid triggering right after login
    // This prevents race conditions with token storage
    setTimeout(() => {
      this.refreshTokenIfNeeded();
    }, 2000); // 2 second delay

    // Set up automatic refresh every 30 minutes (before 24-hour expiration)
    // Only set up automatic refresh if we have a refresh token
    const refreshToken = tokenStorage.getRefreshToken();
    if (refreshToken) {
      this.refreshTimer = setInterval(
        () => {
          this.refreshTokenIfNeeded();
        },
        30 * 60 * 1000 // 30 minutes
      );
    }
  }

  // Stop automatic token refresh
  stopTokenRefresh() {
    this.clearRefreshTimer();
  }

  // Clear the refresh timer
  clearRefreshTimer() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  // Check if token is expired or about to expire
  isTokenExpired() {
    return tokenStorage.isAccessTokenExpired();
  }

  // Check if token is expiring soon
  isTokenExpiringSoon() {
    return tokenStorage.isAccessTokenExpiringSoon();
  }

  // Refresh token if needed
  async refreshTokenIfNeeded() {
    // Guard: prevent concurrent refresh attempts
    if (this.isRefreshing) {
      console.log('⏸️ [tokenManager] Refresh already in progress, skipping...');
      return; // Already refreshing
    }

    // Guard: prevent rapid successive refresh attempts (cooldown)
    const now = Date.now();
    const timeSinceLastAttempt = now - this.lastRefreshAttempt;
    if (timeSinceLastAttempt < this.refreshCooldown) {
      console.log(
        `⏸️ [tokenManager] Refresh cooldown active (${Math.round((this.refreshCooldown - timeSinceLastAttempt) / 1000)}s remaining), skipping...`
      );
      return;
    }

    const state = store.getState();
    const { isAuthenticated } = state.auth;

    if (!isAuthenticated) {
      console.log('⏸️ [tokenManager] Not authenticated, skipping refresh');
      return; // Not authenticated
    }

    // Check if token is expired or about to expire
    const isExpired = this.isTokenExpired();
    const isExpiringSoon = this.isTokenExpiringSoon();

    if (!isExpired && !isExpiringSoon) {
      // Token is still valid, no need to refresh
      return;
    }

    // Check if we have a refresh token for automatic refresh
    const refreshToken = tokenStorage.getRefreshToken();
    if (!refreshToken) {
      console.log('⏸️ [tokenManager] No refresh token available, skipping refresh');
      return; // No refresh token available, but session persists until token expires
    }

    // Set flag and timestamp BEFORE async operation to prevent race conditions
    this.isRefreshing = true;
    this.lastRefreshAttempt = Date.now();
    console.log('🔄 [tokenManager] Starting token refresh...', {
      isExpired,
      isExpiringSoon,
      hasRefreshToken: !!refreshToken,
    });

    try {
      await store.dispatch(refreshTokenAction()).unwrap();
      console.log('✅ [tokenManager] Token refresh successful');
    } catch (error) {
      console.error('❌ [tokenManager] Token refresh failed:', error);
      // Token refresh failed, logout user
      store.dispatch(logoutUser());
    } finally {
      // Always reset flag, even on error
      this.isRefreshing = false;
    }
  }
}

// Create a singleton instance
const tokenManager = new TokenManager();

export default tokenManager;
